import { useCallback, useEffect, useState } from 'react'
import Event from '../Event'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import * as T from '../types'

const DEBOUNCE_DELAY = 500

function debounce<F extends (...args: any[]) => any>(func: F, waitFor: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  const debounced = (...args: Parameters<F>) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => func(...args), waitFor)
  }

  return debounced as (...args: Parameters<F>) => ReturnType<F>
}

const useGetTransactionPreview = (self: any, merchantId: string, customerId?: string, amount?: number) => {
  const [surchargeInfo, setSurchargeInfo] = useState<T.PaymentPreviewData | null>(null)
  const [isSurchargeAvailable, setIsSurchargeAvailable] = useState<boolean>(true)

  const handleResponse = useCallback((response: T.StatusAndResponse<any>) => {
    if (response.status === 'success') {
      setSurchargeInfo(response.data)
      setIsSurchargeAvailable(true)
    } else {
      setSurchargeInfo(null)
      setIsSurchargeAvailable(false)
    }
  }, [])

  const publishRequest = useCallback(() => {
    if (merchantId && customerId !== undefined && amount !== undefined) {
      const request = {
        merchant_id: Number(merchantId),
        customer_id: Number(customerId),
        amount,
      }
      PubSub.pub(Event.GET_TRANSACTION_PREVIEW_INFO_REQUEST, request)
    }
  }, [merchantId, customerId, amount])

  const debouncedPublishRequest = useCallback(debounce(publishRequest, DEBOUNCE_DELAY), [publishRequest])

  useEffect(() => {
    PubSub.sub(self, Event.GET_TRANSACTION_PREVIEW_INFO_RESPONSE, handleResponse)

    if (merchantId && customerId !== undefined && amount !== undefined) {
      debouncedPublishRequest()
    } else {
      setSurchargeInfo(null)
    }

    return () => {
      PubSub.unsub(Event.GET_TRANSACTION_PREVIEW_INFO_RESPONSE)
    }
  }, [self, merchantId, customerId, amount, handleResponse, debouncedPublishRequest]) // Dependencies for the effect

  return surchargeInfo
}

export default useGetTransactionPreview
